import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { InvoiceTemplateInterval } from '~/api/invoiceTemplates/types';

import { Separator, Title } from '~/components';
import { DatePicker, Field, Select } from '~/components/Form';
import { FormBrand, FormClient, FormClientReference, FormNumberingSeries, FormPaymentTerm, FormTitle } from '~/features/documents';

import type { InvoiceTemplateForm } from '../CreateEditInvoiceTemplateForm.types';

import { FormDescription } from '../../components/FormDescription';
import { columnStyle, rowStyle, separatorStyle } from './InvoiceTemplateBasicSection.css';

type Props = {
  form: UseFormReturn<InvoiceTemplateForm>;
};

export const InvoiceTemplateBasicSection = ({ form }: Props) => {
  const { t } = useTranslation(['invoices', 'invoiceTemplates', 'validation']);

  const { control, setValue, watch } = form;

  // Recomputing this each render would hand the date picker a new timestamp every time, which resets its display value.
  const nextInvoiceMinDate = useMemo(() => dayjs().add(1, 'day').toDate(), []);

  const watchInterval = watch('interval');

  const intervalItems = useMemo(
    () =>
      (['Disabled', 'Weekly', 'Monthly', 'Quarterly'] satisfies (InvoiceTemplateInterval | 'Disabled')[]).map((interval) => ({
        label: t(`invoiceTemplates:terminology.interval.cases.${interval}`),
        value: interval,
      })),
    [t],
  );

  return (
    <div className={rowStyle}>
      {/* Column 1 — title, description & client */}
      <div className={columnStyle}>
        <Title as="h2" styleAs="h3">
          {t('invoices:createEdit.basicInfo.title')}
        </Title>

        <FormTitle form={form} />
        <FormDescription form={form} />

        {/* Client */}
        <FormClient domain="invoices" form={form} required={false} />
      </div>

      <Separator className={separatorStyle} orientation="vertical" />

      {/* Column 2 — invoice details */}
      <div className={columnStyle}>
        <Title as="h2" styleAs="h3">
          {t('invoices:createEdit.invoiceDetails.title')}
        </Title>

        <FormNumberingSeries enabledFor="invoices" form={form} required={false} showHelpText={false} />

        <FormPaymentTerm form={form} helpText="invoiceTemplate" />

        {/* Interval (Herhaling) */}
        <Controller
          control={control}
          name="interval"
          render={({ field: { name, onChange, value } }) => (
            <Field.Root name={name}>
              <Field.Label>{t('invoiceTemplates:createEdit.fields.interval.label')}</Field.Label>

              <Select.Root
                items={intervalItems}
                onValueChange={(newInterval) => {
                  const interval = newInterval === 'Disabled' ? null : newInterval;

                  onChange(interval);
                  if (interval === null) setValue('nextInvoiceAt', null);
                }}
                value={value ?? 'Disabled'}
              >
                <Select.Button />
                <Select.List>
                  {intervalItems.map((item) => (
                    <Select.Item key={String(item.value)} value={item.value}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>
            </Field.Root>
          )}
        />

        {watchInterval !== null && (
          <Controller
            control={control}
            name="nextInvoiceAt"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('invoiceTemplates:createEdit.fields.nextInvoiceAt.label')}</Field.Label>

                <DatePicker minDate={nextInvoiceMinDate} onBlur={onBlur} onChange={onChange} ref={ref} value={value} />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: (value) => dayjs(value).isAfter(dayjs().endOf('day')) || t('validation:invoiceTemplate.nextInvoiceTomorrow'),
            }}
          />
        )}

        <FormBrand form={form} />
        <FormClientReference form={form} />
      </div>
    </div>
  );
};
